import PropTypes from "prop-types";
import { t } from "@/i18n-js/instance";
import { useThemeContext } from "@/react/providers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { toLocaleString } from "@circle-react/helpers/number";
import {
  isPrivateSpace,
  isSecretSpace,
} from "@circle-react/helpers/spaceHelpers";
import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";
import { JoinCourse } from "./JoinCourse";
import { ProgressStatus } from "./ProgressStatus";

export const AdminDetails = ({ course }) => {
  const { students_count: studentsCount } = course;
  const isPrivate = isPrivateSpace(course);

  return (
    <>
      {!isPrivate && (
        <div
          className="text-default flex items-center gap-x-2 px-1"
          title={t("courses.admin_summary.students")}
          aria-label={t("courses.admin_summary.students")}
        >
          <Icon type="20-members" size={16} />
          <Typography.LabelSm color="text-default" weight="medium">
            {toLocaleString(studentsCount)}
          </Typography.LabelSm>
        </div>
      )}
    </>
  );
};

export const UserDetails = ({ course }) => {
  const { joined } = course;

  if (joined) {
    return <ProgressStatus course={course} />;
  }

  return <JoinCourse space={course} />;
};

export const CourseCardDetails = ({ course, communityMember }) => {
  const {
    name,
    emoji,
    custom_emoji_url: customEmojiUrl,
    custom_emoji_dark_url: customEmojiDarkUrl,
    space_group: { name: spaceGroupName },
  } = course;
  const { currentAppearance } = useThemeContext();
  const isPrivate = isPrivateSpace(course);
  const isSecret = isSecretSpace(course);
  const privateLabel = isSecret
    ? t("courses.directory.gated_course")
    : t("courses.directory.private_course");

  return (
    <div className="flex flex-col gap-y-2 pb-1 pt-4">
      <div className="flex h-20 flex-col gap-y-2">
        <div className="flex items-start">
          <Typography.TitleSm as="h1" weight="semibold">
            <span
              title={name}
              className="ml-1 line-clamp-2 min-w-0 overflow-hidden text-base font-bold leading-tight"
            >
              <EmojiRenderer
                className="-mt-1 mr-1 inline-flex !h-5 !w-5"
                emoji={emoji}
                appearance={currentAppearance}
                customEmojiUrl={customEmojiUrl}
                customEmojiDarkUrl={customEmojiDarkUrl}
              />
              {name}
            </span>
          </Typography.TitleSm>
        </div>
        <Typography.BodySm weight="medium" truncate>
          <span className="text-default truncate px-1 text-sm font-medium">
            {spaceGroupName}
          </span>
        </Typography.BodySm>
      </div>

      {isCommunityAdmin(communityMember) ? (
        <AdminDetails course={course} />
      ) : (
        <UserDetails course={course} />
      )}
      {isPrivate && (
        <div className="flex flex-col gap-y-4">
          <div
            aria-label={privateLabel}
            title={privateLabel}
            className="text-default flex items-center gap-x-1"
          >
            <Icon
              type="16-lock"
              size={16}
              useWithFillCurrentColor
              aria-hidden
            />
            <Typography.LabelSm weight="medium">
              {privateLabel}
            </Typography.LabelSm>
          </div>
        </div>
      )}
    </div>
  );
};

UserDetails.propTypes = {
  course: PropTypes.shape({
    joined: PropTypes.bool,
    lessons_count: PropTypes.number,
  }),
};

AdminDetails.propTypes = {
  course: PropTypes.shape({
    students_count: PropTypes.number,
  }).isRequired,
};

CourseCardDetails.propTypes = {
  communityMember: PropTypes.object,
  course: PropTypes.shape({
    emoji: PropTypes.string,
    custom_emoji_url: PropTypes.string,
    custom_emoji_dark_url: PropTypes.string,
    name: PropTypes.string,
    space_group: PropTypes.shape({
      name: PropTypes.string,
    }),
    joined: PropTypes.bool,
    created_at: PropTypes.string,
    is_private: PropTypes.bool,
    is_draft: PropTypes.bool,
    visibility: PropTypes.string,
    lessons_count: PropTypes.number,
    students_count: PropTypes.number,
    completed_lessons_count: PropTypes.number,
  }),
};
